import React, { useState } from 'react';
import { useQuery } from 'react-query';

import styled from 'styled-components';

import { validateShopRequest } from 'apis/ShopRequestAPI';
import { useRootContext } from 'components/layout/RootContext';
import Button from 'components/lib/Button';
import { isMysteryShopAdmin } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';

import { useEditShopRequestModalContext } from './EditShopRequestModalContext';
import { SHOP_REQUEST_PROPAGATION_MODAL_MODE, SHOP_REQUEST_STATUS_LABELS, SHOP_REQUEST_TYPE } from '../constants';
import ShopRequestPropagateModal from '../propagate/ShopRequestPropagateModal';
import { getShopRequestPropagationCommunities } from '../utils';
import ShopRequestValidationModal, { ShopRequestValidationWarning } from '../Validation/ShopRequestValidationModal';

const Container = styled.div`
  display: flex;
  flex-flow: row;
  justify-content: space-between;
  width: 100%;
`;

const StyledDangerButton = styled(Button)`
  color: var(--negative-orange);
  border-color: var(--negative-orange);
  background-color: rgba(250, 117, 80, 0.09);
`;

const CommunityName = styled.span`
  color: var(--dark-blue);
`;

type Props = {
  onOk: () => void;
  onCancel: () => void;
  onDelete: () => void;
};

const EditShopRequestModalFooter = ({ onOk, onCancel, onDelete }: Props) => {
  const { currentUser } = useAppSelector((state) => state.auth);
  const { showMessage } = useRootContext();
  const { shopRequest, originalShopRequest, isLoading } = useEditShopRequestModalContext();

  const [showPropagationModal, setShowPropagationModal] = useState(false);
  const [validationWarnings, setValidationWarnings] = useState<ShopRequestValidationWarning[]>([]);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [pendingValidationAction, setPendingValidationAction] = useState<(() => void) | null>(null);

  const { refetch: refetchValidation, isFetching: isValidationLoading } = useQuery({
    queryKey: ['shopRequestValidation', shopRequest?.id],
    queryFn: () => validateShopRequest(shopRequest?.id as number),
    enabled: false,
    refetchOnWindowFocus: false,
  });

  const isShopRequestAdmin = currentUser && isMysteryShopAdmin(currentUser);
  const isPricingRequest = shopRequest?.request_type === SHOP_REQUEST_TYPE.MYSTERY_SHOP;
  const baseCommunityId = shopRequest?.requestGroup?.base_community?.id ?? shopRequest?.requestGroup?.base_community_id;

  const handleValidationClose = () => {
    setShowValidationModal(false);
    setValidationWarnings([]);
    setPendingValidationAction(null);
  };

  const handleValidationConfirm = () => {
    setShowValidationModal(false);
    const action = pendingValidationAction;
    setPendingValidationAction(null);
    setValidationWarnings([]);
    if (action) {
      action();
    }
  };

  const runValidation = async (action: () => void) => {
    setPendingValidationAction(() => action);
    try {
      const result = await refetchValidation();
      const warnings = Array.isArray(result.data) ? result.data : [];
      if (warnings.length > 0) {
        setValidationWarnings(warnings);
        setShowValidationModal(true);
        return;
      }
      setPendingValidationAction(null);
      action();
    } catch (error) {
      setPendingValidationAction(null);
      showMessage('error', 'Failed to validate shop request');
    }
  };

  const onConfirm = () => {
    const requestsHaveDifferentStatuses = originalShopRequest?.status !== shopRequest?.status;
    const requestsHadApprovedStatus = [shopRequest?.status].includes(SHOP_REQUEST_STATUS_LABELS.APPROVED);

    const proceed = () => {
      if (isPricingRequest && requestsHaveDifferentStatuses && requestsHadApprovedStatus) {
        setShowPropagationModal(true);
      } else {
        onOk();
      }
    };

    const requiresValidation =
      requestsHaveDifferentStatuses &&
      [SHOP_REQUEST_STATUS_LABELS.NEEDS_APPROVAL, SHOP_REQUEST_STATUS_LABELS.APPROVED].includes(
        shopRequest?.status || 0
      );

    if (requiresValidation) {
      runValidation(proceed);
      return;
    }

    proceed();
  };

  const communities = getShopRequestPropagationCommunities(shopRequest);

  return (
    <Container>
      {isShopRequestAdmin ? (
        <StyledDangerButton danger onClick={onDelete} disabled={isLoading} data-test-id="sr-modal-delete">
          Delete
        </StyledDangerButton>
      ) : (
        <div />
      )}
      <div>
        <Button type="primary" onClick={onConfirm} disabled={isLoading} data-test-id="sr-modal-save">
          Save
        </Button>
        <Button onClick={onCancel} data-test-id="sr-modal-cancel">
          Close
        </Button>
      </div>
      {showPropagationModal && (
        <ShopRequestPropagateModal
          mode={SHOP_REQUEST_PROPAGATION_MODAL_MODE.APPROVE}
          subtitle={<CommunityName>{shopRequest?.community?.name}</CommunityName>}
          baseCommunityId={baseCommunityId}
          communities={communities}
          open={showPropagationModal}
          onConfirm={onOk}
          onCancel={() => setShowPropagationModal(false)}
        />
      )}
      {showValidationModal && (
        <ShopRequestValidationModal
          open={showValidationModal}
          warnings={validationWarnings}
          onConfirm={handleValidationConfirm}
          onClose={handleValidationClose}
          confirmLoading={isValidationLoading}
        />
      )}
    </Container>
  );
};

export default EditShopRequestModalFooter;
